import React from "react";

import { Icon, type IconName } from "./icons";
import { Floating, listKey, usePopover } from "./popover";

export type Option<T extends string = string> = {
  value: T;
  label: string;
  hint?: string;
  disabled?: boolean;
};

/** Dropdown for picking one value. ``label`` prints inside the trigger
 * ("Breakdown: Agent"); without it, pass ``ariaLabel``. */
export function Select<T extends string>({
  value,
  options,
  onChange,
  label,
  ariaLabel,
  icon,
  size = "md",
  disabled,
  block,
  className,
  placeholder,
  quiet,
}: {
  value: T;
  options: Option<T>[];
  onChange: (v: T) => void;
  label?: string;
  ariaLabel?: string;
  icon?: IconName;
  size?: "sm" | "md";
  disabled?: boolean;
  /** Fill the container's width, as in a form. */
  block?: boolean;
  className?: string;
  placeholder?: string;
  /** No border until hover or focus, for a control repeated down a table. */
  quiet?: boolean;
}) {
  const pop = usePopover<HTMLButtonElement>();
  const list = React.useRef<HTMLUListElement | null>(null);
  const typed = React.useRef({ text: "", at: 0 });
  const id = React.useId();
  const current = options.find((o) => o.value === value);
  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    if (!pop.open) return;
    setActive(Math.max(0, options.indexOf(current as Option<T>)));
    list.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focus on open only
  }, [pop.open]);

  React.useEffect(() => {
    if (!pop.open) return;
    list.current
      ?.querySelector<HTMLElement>(`[data-i="${active}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [active, pop.open]);

  function pick(i: number) {
    const o = options[i];
    if (!o || o.disabled) return;
    onChange(o.value);
    pop.close();
  }

  return (
    <div
      ref={pop.root}
      className={`ds-select ${size}${block ? " block" : ""}${quiet ? " quiet" : ""}${className ? ` ${className}` : ""}`}
    >
      <button
        ref={pop.trigger}
        type="button"
        className="ds-select-btn"
        aria-haspopup="listbox"
        aria-expanded={pop.open}
        aria-controls={pop.open ? id : undefined}
        aria-label={
          ariaLabel ?? (label ? `${label}: ${current?.label ?? ""}` : undefined)
        }
        disabled={disabled}
        onClick={pop.toggle}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            if (!pop.open) pop.toggle();
          }
        }}
      >
        {icon && <Icon name={icon} size={size === "sm" ? 14 : 16} />}
        {label && <span className="ds-select-label">{label}:</span>}
        <span className="ds-select-value">
          {current?.label ?? placeholder ?? ""}
        </span>
        <Icon name="chevron-down" size={14} className="ds-select-chev" />
      </button>
      {pop.open && (
        <Floating>
          <ul
            ref={(el) => {
              list.current = el;
              pop.panel.current = el;
            }}
            style={pop.style}
            id={id}
            role="listbox"
            tabIndex={-1}
            className="ds-pop"
            aria-label={ariaLabel ?? label}
            aria-activedescendant={`${id}-${active}`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                pick(active);
                return;
              }
              if (e.key === "Tab") {
                e.preventDefault();
                pop.close();
                return;
              }
              const next = listKey(
                e.key,
                active,
                options.map((o) => o.label),
                typed,
              );
              if (next != null) {
                e.preventDefault();
                setActive(next);
              }
            }}
          >
            {options.map((o, i) => (
              <li
                key={o.value}
                id={`${id}-${i}`}
                data-i={i}
                role="option"
                aria-selected={o.value === value}
                aria-disabled={o.disabled || undefined}
                className={`ds-pop-item${i === active ? " active" : ""}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(i)}
              >
                <span className="ds-pop-text">
                  {o.label}
                  {o.hint && <span className="ds-pop-hint">{o.hint}</span>}
                </span>
                {o.value === value && <Icon name="check" size={14} />}
              </li>
            ))}
          </ul>
        </Floating>
      )}
    </div>
  );
}

export const Input = React.forwardRef<
  HTMLInputElement,
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & {
    icon?: IconName;
    size?: "sm" | "md";
    block?: boolean;
  }
>(function Input({ icon, size = "md", block, className, ...rest }, ref) {
  return (
    <span
      className={`ds-input ${size}${block ? " block" : ""}${className ? ` ${className}` : ""}`}
    >
      {icon && <Icon name={icon} size={14} />}
      <input ref={ref} {...rest} />
    </span>
  );
});

/** Dollar amount entry: "$" prefix, decimal keyboard, optional suffix. */
export const MoneyInput = React.forwardRef<
  HTMLInputElement,
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & {
    suffix?: string;
    size?: "sm" | "md";
  }
>(function MoneyInput({ suffix, size = "md", className, ...rest }, ref) {
  const errId = React.useId();
  const problem = moneyProblem(String(rest.value ?? ""));
  // Always wrapped, so showing the error never remounts the focused input.
  return (
    <span className="ds-money">
      <span
        className={`ds-input money ${size}${problem ? " invalid" : ""}${className ? ` ${className}` : ""}`}
      >
        <span className="ds-input-affix">$</span>
        <input
          ref={ref}
          inputMode="decimal"
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? errId : undefined}
          {...rest}
        />
        {suffix && <span className="ds-input-affix">{suffix}</span>}
      </span>
      {problem && (
        <span id={errId} className="ds-field-error" role="alert">
          {problem}
        </span>
      )}
    </span>
  );
});

/** The typed dollar amount as a number: NaN when ``moneyProblem`` objects,
 * null when the field is empty. Grouping commas are allowed. */
export function parseMoney(raw: string): number | null {
  const v = raw.trim();
  if (v === "") return null;
  return moneyProblem(v) ? NaN : Number(v.replace(/,/g, ""));
}

/** Why a typed dollar amount can't be used, or null when it reads as one. */
export function moneyProblem(raw: string): string | null {
  const v = raw.trim();
  if (/^[\d,]*(\.\d{0,2})?$/.test(v)) return null;
  if (v.startsWith("-")) return "Amounts can't be negative.";
  if (/\.\d{3}/.test(v) && !/\..*\./.test(v))
    return "Use at most two decimals.";
  return "Use digits only, like 250 or 99.50.";
}

/** A form row: label above the control, optional hint or error below. */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label className="ds-field">
      <span className="ds-field-label">{label}</span>
      {children}
      {error ? (
        <span className="ds-field-error" role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="ds-field-hint">{hint}</span>
      )}
    </label>
  );
}
